/**
 * Forms Management permissions.
 *
 * Two layers:
 *  1. Module capability — Admin / FormsAdmin get everything; other users get a
 *     capability when their role grants it by default OR they hold the matching
 *     `Forms.*` right (rights come from the identity service and can be granted
 *     per user without a role).
 *  2. Row level — every form the API returns carries `accessLevelID`
 *     (1 Viewer, 2 Editor, 3 Owner). `forForm(level)` combines both so a page can
 *     decide, per form, whether to show the builder / settings / publish buttons.
 *
 * The UI only hides what the user cannot do; the API enforces the same rules.
 */

import { useCallback, useMemo } from 'react';
import { useEnrichedAuth } from '../useEnrichedAuth';

export const FORM_ACCESS_LEVEL = {
  VIEWER: 1,
  EDITOR: 2,
  OWNER: 3,
} as const;

export const FORMS_RIGHTS = {
  READ: 'Forms.Read',
  CREATE: 'Forms.Create',
  EDIT: 'Forms.Edit',
  DELETE: 'Forms.Delete',
  PUBLISH: 'Forms.Publish',
  ENTRIES_READ: 'Forms.Entries.Read',
  ENTRIES_EDIT: 'Forms.Entries.Edit',
  ENTRIES_DELETE: 'Forms.Entries.Delete',
  ENTRIES_APPROVE: 'Forms.Entries.Approve',
  ENTRIES_EXPORT: 'Forms.Entries.Export',
  ENTRIES_VIEW_SENSITIVE: 'Forms.Entries.ViewSensitive',
  MEMBERS_CONVERT: 'Forms.Members.Convert',
  MEMBERS_MERGE: 'Forms.Members.Merge',
  WEBHOOKS_MANAGE: 'Forms.Webhooks.Manage',
  SETTINGS_MANAGE: 'Forms.Settings.Manage',
  AI_USE: 'Forms.Ai.Use',
  API_ACCESS_MANAGE: 'Forms.ApiAccess.Manage',
} as const;

export const FORMS_ALL_ROLES = ['Admin', 'FormsAdmin', 'FormsManager', 'FormsReviewer', 'FormsViewer'];
export const FORMS_ADMIN_ROLES = ['Admin', 'FormsAdmin'];

export interface FormRowPermissions {
  accessLevelID: number;
  isOwner: boolean;
  /** May open the builder / settings / share tabs and save. */
  canEditForm: boolean;
  canPublishForm: boolean;
  canDeleteForm: boolean;
  canManageCollaborators: boolean;
  canReadFormEntries: boolean;
  canEditFormEntries: boolean;
  canApproveFormEntries: boolean;
  canExportFormEntries: boolean;
  canManageFormApiAccess: boolean;
}

export function useFormsPermissions() {
  const { user, hasRole, hasPermission } = useEnrichedAuth();

  const isFormsAdmin = hasRole('Admin') || hasRole('FormsAdmin');
  const isManager = hasRole('FormsManager');
  const isReviewer = hasRole('FormsReviewer');
  const isViewer = hasRole('FormsViewer');

  /** Capability = admin, or a role that grants it by default, or the explicit right. */
  const can = useCallback(
    (right: string, roleDefault: boolean) => isFormsAdmin || roleDefault || hasPermission(right),
    [isFormsAdmin, hasPermission]
  );

  const perms = useMemo(() => {
    const canRead = can(FORMS_RIGHTS.READ, isManager || isReviewer || isViewer);
    const canCreate = can(FORMS_RIGHTS.CREATE, isManager);
    const canEdit = can(FORMS_RIGHTS.EDIT, isManager);
    const canDelete = can(FORMS_RIGHTS.DELETE, isManager);
    const canPublish = can(FORMS_RIGHTS.PUBLISH, isManager);
    const canReadEntries = can(FORMS_RIGHTS.ENTRIES_READ, isManager || isReviewer || isViewer);
    const canEditEntries = can(FORMS_RIGHTS.ENTRIES_EDIT, isManager);
    const canDeleteEntries = can(FORMS_RIGHTS.ENTRIES_DELETE, isManager);
    const canApprove = can(FORMS_RIGHTS.ENTRIES_APPROVE, isManager || isReviewer);
    const canExport = can(FORMS_RIGHTS.ENTRIES_EXPORT, isManager || isReviewer);
    const canViewSensitive = can(FORMS_RIGHTS.ENTRIES_VIEW_SENSITIVE, false);
    const canConvert = can(FORMS_RIGHTS.MEMBERS_CONVERT, isManager);
    const canMerge = can(FORMS_RIGHTS.MEMBERS_MERGE, false);
    const canManageWebhooks = can(FORMS_RIGHTS.WEBHOOKS_MANAGE, false);
    const canManageSettings = can(FORMS_RIGHTS.SETTINGS_MANAGE, false);
    const canUseAi = can(FORMS_RIGHTS.AI_USE, isManager);
    const canManageApiAccess = can(FORMS_RIGHTS.API_ACCESS_MANAGE, false);
    return {
      canRead,
      canCreate,
      canEdit,
      canDelete,
      canPublish,
      canReadEntries,
      canEditEntries,
      canDeleteEntries,
      canApprove,
      canExport,
      canViewSensitive,
      canConvert,
      canMerge,
      canManageWebhooks,
      canManageSettings,
      canUseAi,
      canManageApiAccess,
    };
  }, [can, isManager, isReviewer, isViewer]);

  /** Row-level permissions for one form given the API's accessLevelID. */
  const forForm = useCallback(
    (accessLevelID?: number | null): FormRowPermissions => {
      const level = isFormsAdmin ? FORM_ACCESS_LEVEL.OWNER : Number(accessLevelID ?? 0);
      const isEditor = level >= FORM_ACCESS_LEVEL.EDITOR;
      const isOwner = level >= FORM_ACCESS_LEVEL.OWNER;
      return {
        accessLevelID: level,
        isOwner,
        canEditForm: perms.canEdit && isEditor,
        canPublishForm: perms.canPublish && isEditor,
        canDeleteForm: perms.canDelete && isOwner,
        canManageCollaborators: perms.canEdit && isOwner,
        canReadFormEntries: perms.canReadEntries && level >= FORM_ACCESS_LEVEL.VIEWER,
        canEditFormEntries: perms.canEditEntries && isEditor,
        canApproveFormEntries: perms.canApprove && level >= FORM_ACCESS_LEVEL.VIEWER,
        canExportFormEntries: perms.canExport && level >= FORM_ACCESS_LEVEL.VIEWER,
        canManageFormApiAccess: perms.canManageApiAccess && isEditor,
      };
    },
    [isFormsAdmin, perms]
  );

  return {
    ...perms,
    isFormsAdmin,
    isFormsManager: isManager,
    isFormsReviewer: isReviewer,
    isFormsViewer: isViewer,
    userId: user?.id as string | undefined,
    forForm,
  };
}

export type FormsPermissions = ReturnType<typeof useFormsPermissions>;
